/**
 * The room's guardrails on the host screen, under every game: lock the room, the
 * profanity filter, and removed players asking back in. Core's, so no game has to
 * carry them. The session owner's only, like the routes behind them.
 */

import { useEffect, useState } from 'react';
import type { Snapshot } from '@gsf/contract';
import { api } from '../lib/api';

export interface RoomControlsProps {
  readonly snapshot: Snapshot;
  readonly notify: (message: string) => void;
}

export function RoomControls({ snapshot, notify }: RoomControlsProps) {
  const { core, you } = snapshot;
  const [busy, setBusy] = useState(false);

  // A request the host leaves alone lapses into a denial; drop it from view when it does.
  const [now, setNow] = useState(() => Date.now());
  const waiting = you.rejoinRequests.filter((r) => r.expiresAt > now);
  useEffect(() => {
    if (you.rejoinRequests.length === 0) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [you.rejoinRequests.length]);

  if (!you.canManageSession) return null;

  async function run(what: string, call: () => Promise<unknown>) {
    setBusy(true);
    try {
      await call();
    } catch (err) {
      notify(err instanceof Error ? err.message : `Could not ${what}.`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="room-controls">
      <label className="host-toggle" title="New players cannot join; anyone already in can reconnect">
        <input
          type="checkbox"
          checked={core.locked}
          disabled={busy}
          onChange={(e) => void run('lock the room', () => api.setRoom(core.code, { locked: e.target.checked }))}
        />
        Lock room
      </label>
      <label className="host-toggle" title="Mask profanity on the TV and phones; you still see what was typed">
        <input
          type="checkbox"
          checked={core.profanityFilter}
          disabled={busy}
          onChange={(e) =>
            void run('change the filter', () => api.setRoom(core.code, { profanityFilter: e.target.checked }))
          }
        />
        Profanity filter
      </label>
      {waiting.length > 0 ? (
        <ul className="rejoin-requests" aria-label="Requests to rejoin">
          {waiting.map((r) => (
            <li key={r.id}>
              <span>
                <strong>{r.name}</strong> asks to rejoin
              </span>
              <button
                className="btn btn-sm btn-primary"
                disabled={busy}
                onClick={() => void run('let them in', () => api.decideRejoin(core.code, r.id, true))}
              >
                Let in
              </button>
              <button
                className="btn btn-sm"
                disabled={busy}
                onClick={() => void run('deny that', () => api.decideRejoin(core.code, r.id, false))}
              >
                Deny entry
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
